import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Moon, Search, Sun, X } from "lucide-react";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import {
  getTableAvailability,
  getTablePlanning,
  getSessionPartners,
  startPlayerSession,
  joinPlayerSession,
} from "./funtionsBD/Mainservice";
import { persistLiveTrainingSession } from "../hooks/useLiveTrainingRedirect";
import { requestLiveFullscreen } from "../hooks/useLiveFullscreen";
import { isMobileDevice } from "../utils/isMobileDevice";
import { useBrand } from "../context/BrandContext";
import {
  MEMBER_HOURS_END,
  MEMBER_HOURS_START,
  MAX_SCHEDULE_WEEKS,
  SLOT_MINUTES,
  addWeeks,
  formatSlotTime,
  formatWeekRange,
  heatmapSlotsForDay,
  isSlotInPast,
  isSlotLiveNow,
  slotEnd,
  slotFitsMemberHours,
  slotOccupancy,
  snapToNextSlot,
  nextOpenMemberSlot,
  startOfWeekMonday,
  startOfDay,
  toDateKey,
  weekDays,
  intervalsOverlap,
  slotStartsForDay,
} from "../utils/playerSessionWeek";
import { formatCalendarDay } from "../utils/sessionCalendarAgenda";
import "./PlayerNewTrainingSessionDialog.css";

const DURATION_OPTIONS = [30, 45, 60, 90];

function foldName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function partnerLabel(p) {
  const name = `${p?.user_first_name || ""} ${p?.user_last_name || ""}`.trim();
  const alias = String(p?.user_alias || "").trim();
  if (name) {
    return alias && foldName(alias) !== foldName(name) ? `${name} (${alias})` : name;
  }
  return alias || p?.player_id || "—";
}

function partnerMatchesQuery(p, q) {
  const needle = foldName(q);
  if (!needle) return false;
  const first = foldName(p?.user_first_name);
  const last = foldName(p?.user_last_name);
  const alias = foldName(p?.user_alias);
  const tokens = needle.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    const hay = `${first} ${last} ${alias}`;
    return tokens.every((tok) => hay.includes(tok));
  }
  return (
    first.includes(needle) ||
    last.includes(needle) ||
    alias.includes(needle) ||
    `${first} ${last}`.includes(needle)
  );
}

function extractTablePayload(res) {
  const body = res?.data;
  if (typeof body === "string") {
    const trimmed = body.trim();
    if (!trimmed || trimmed.startsWith("<")) {
      throw { status: "error", message: "Invalid table planning response" };
    }
  }
  if (!body || typeof body !== "object") return {};
  if (body.status === "error") {
    throw body;
  }
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return body.data;
  }
  return body;
}

function occupancyLabel(row, t, locale) {
  const table =
    row.table_number != null
      ? `${t("playerSessionTable")} ${row.table_number}`
      : t("playerSessionTable");
  const start = row.starts_at
    ? formatSlotTime(row.starts_at, locale)
    : "";
  const end = row.ends_at ? formatSlotTime(row.ends_at, locale) : "";
  const who =
    row.kind === "rental"
      ? t("playerSessionRental")
      : row.looking_for_partner
        ? row.owner_name
          ? `${row.owner_name} · ${t("playerSessionLookingForPartner")}`
          : t("playerSessionLookingForPartner")
      : row.owner_name && row.ts_owner_player_id
        ? row.owner_name
        : row.group_name ||
          (row.ts_owner_player_id ? t("playerSessionPersonal") : t("playerSessionGroup"));
  return `${table} — ${who} ${start}–${end}`;
}

function currentPlayerId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return String(u.player_id || u.tbl_player?.player_id || "").trim();
  } catch {
    return "";
  }
}

export function PlayerNewTrainingSessionDialog({
  open,
  onClose,
  grId,
  clubId,
  onStarted,
  variant = "home",
}) {
  const { t, i18n } = useTranslation();
  const { themeMode, setThemeMode } = useBrand();
  const locale = i18n.language || undefined;
  const [duration, setDuration] = useState(60);
  const [tableId, setTableId] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const [selectedStart, setSelectedStart] = useState(() => snapToNextSlot());
  const [freeTables, setFreeTables] = useState([]);
  const [occupancy, setOccupancy] = useState([]);
  const [weekOccupancy, setWeekOccupancy] = useState([]);
  const [clubTables, setClubTables] = useState([]);
  const [tableCount, setTableCount] = useState(1);
  const [partners, setPartners] = useState([]);
  const [partnerQuery, setPartnerQuery] = useState("");
  const [lookingForPartner, setLookingForPartner] = useState(false);
  const [joiningTsId, setJoiningTsId] = useState("");
  const [partnerMenuOpen, setPartnerMenuOpen] = useState(false);
  const [partnerHighlight, setPartnerHighlight] = useState(0);
  const [searchingPartners, setSearchingPartners] = useState(false);
  const partnerFieldRef = useRef(null);
  const [loadingWeek, setLoadingWeek] = useState(false);
  const [loadingSlot, setLoadingSlot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const appliedDefaultRef = useRef(false);

  const days = useMemo(() => weekDays(weekStart), [weekStart]);
  const heatRows = useMemo(
    () => heatmapSlotsForDay(days[0] || new Date()),
    [days]
  );
  const selectedEnd = useMemo(
    () => slotEnd(selectedStart, duration),
    [selectedStart, duration]
  );
  const liveNow = isSlotLiveNow(selectedStart);
  const matchedPartners = useMemo(() => {
    const q = partnerQuery.trim();
    if (!q) return [];
    return partners.filter((p) => partnerMatchesQuery(p, q)).slice(0, 20);
  }, [partners, partnerQuery]);
  const selectedPartner = useMemo(
    () => partners.find((p) => String(p.player_id) === String(partnerId)) || null,
    [partners, partnerId]
  );
  const slotSeekers = useMemo(() => {
    const me = currentPlayerId();
    const rows = [...(occupancy || []), ...(weekOccupancy || [])];
    const seen = new Set();
    const out = [];
    for (const row of rows) {
      if (!row?.looking_for_partner || !row.ts_id) continue;
      if (
        !intervalsOverlap(row.starts_at, row.ends_at, selectedStart, selectedEnd)
      ) {
        continue;
      }
      if (me && String(row.ts_owner_player_id) === me) continue;
      const key = String(row.ts_id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
    return out;
  }, [occupancy, weekOccupancy, selectedStart, selectedEnd]);
  const thisWeekStart = startOfWeekMonday(new Date());
  const canGoPrev =
    weekStart.getTime() > addWeeks(thisWeekStart, -1).getTime();
  const canGoNext =
    weekStart.getTime() < addWeeks(thisWeekStart, MAX_SCHEDULE_WEEKS - 1).getTime();

  const loadWeek = useCallback(async () => {
    if (!open) return;
    setLoadingWeek(true);
    setError("");
    try {
      const from = toDateKey(days[0]);
      const to = toDateKey(days[6]);
      const plan = await getTablePlanning({
        gr_id: grId || undefined,
        club_id: clubId || undefined,
        from,
        to,
      });
      const data = extractTablePayload(plan);
      const occ = data.occupancy || [];
      const hallTables = Array.isArray(data.tables) ? data.tables : [];
      setWeekOccupancy(occ);
      setClubTables(hallTables);
      const listed = hallTables.length;
      const busy = new Set(occ.map((row) => row?.table_id).filter(Boolean)).size;
      const count = Math.max(1, listed, busy);
      setTableCount(count);
      if (!appliedDefaultRef.current) {
        const next = nextOpenMemberSlot({
          occupancy: occ,
          tableCount: count,
          durationMinutes: 60,
        });
        setDuration(60);
        setSelectedStart(next);
        setWeekStart(startOfWeekMonday(next));
        appliedDefaultRef.current = true;
      }
    } catch (err) {
      setError(err?.message || t("playerSessionLoadError"));
      setWeekOccupancy([]);
      setClubTables([]);
    } finally {
      setLoadingWeek(false);
    }
  }, [open, grId, clubId, days, t]);

  const loadSlot = useCallback(async () => {
    if (!open || !selectedStart) return;
    setLoadingSlot(true);
    try {
      const [availResult, partnerResult] = await Promise.allSettled([
        getTableAvailability({
          gr_id: grId || undefined,
          club_id: clubId || undefined,
          duration_minutes: duration,
          starts_at: selectedStart.toISOString(),
        }),
        getSessionPartners({
          gr_id: grId || undefined,
          club_id: clubId || undefined,
        }),
      ]);
      if (availResult.status !== "fulfilled") {
        throw availResult.reason;
      }
      const avail = availResult.value;
      const partnerRes =
        partnerResult.status === "fulfilled" ? partnerResult.value : null;
      const availData = extractTablePayload(avail);
      let free = Array.isArray(availData.free_tables)
        ? availData.free_tables
        : [];
      const liveOcc = Array.isArray(availData.occupancy)
        ? availData.occupancy
        : [];
      const windowOcc = slotOccupancy({
        occupancy: liveOcc.length ? liveOcc : weekOccupancy,
        tableCount: Math.max(tableCount, clubTables.length, 1),
        slotStart: selectedStart,
        slotEnd: selectedEnd,
      });
      if (!free.length && windowOcc.status !== "full" && clubTables.length) {
        const busyIds = new Set();
        for (const row of liveOcc.length ? liveOcc : weekOccupancy) {
          if (!row?.table_id) continue;
          if (
            intervalsOverlap(
              row.starts_at,
              row.ends_at,
              selectedStart,
              selectedEnd
            )
          ) {
            busyIds.add(String(row.table_id));
          }
        }
        free = clubTables.filter((tb) => !busyIds.has(String(tb.table_id)));
      }
      setError("");
      setFreeTables(free);
      setOccupancy(liveOcc);
      setPartners(partnerRes?.data?.data?.partners || partnerRes?.data?.partners || []);
      setTableId((prev) => {
        if (prev && free.some((tb) => String(tb.table_id) === String(prev))) {
          return prev;
        }
        return free[0]?.table_id ? String(free[0].table_id) : "";
      });
    } catch (err) {
      const windowOcc = slotOccupancy({
        occupancy: weekOccupancy,
        tableCount: Math.max(tableCount, clubTables.length, 1),
        slotStart: selectedStart,
        slotEnd: selectedEnd,
      });
      if (windowOcc.status !== "full" && clubTables.length) {
        const busyIds = new Set();
        for (const row of weekOccupancy) {
          if (!row?.table_id) continue;
          if (
            intervalsOverlap(
              row.starts_at,
              row.ends_at,
              selectedStart,
              selectedEnd
            )
          ) {
            busyIds.add(String(row.table_id));
          }
        }
        const free = clubTables.filter((tb) => !busyIds.has(String(tb.table_id)));
        setFreeTables(free);
        setTableId(free[0]?.table_id ? String(free[0].table_id) : "");
        setError("");
      } else {
        setError(err?.message || t("playerSessionLoadError"));
        setFreeTables([]);
      }
    } finally {
      setLoadingSlot(false);
    }
  }, [
    open,
    grId,
    clubId,
    duration,
    selectedStart,
    selectedEnd,
    t,
    weekOccupancy,
    clubTables,
    tableCount,
  ]);

  useEffect(() => {
    if (!open) return;
    appliedDefaultRef.current = false;
    const next = snapToNextSlot();
    setDuration(60);
    setSelectedStart(next);
    setWeekStart(startOfWeekMonday(next));
    setPartnerId("");
    setPartnerQuery("");
    setLookingForPartner(false);
    setJoiningTsId("");
    setPartnerMenuOpen(false);
    setPartnerHighlight(0);
    setError("");
  }, [open]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  useEffect(() => {
    loadSlot();
  }, [loadSlot]);

  useEffect(() => {
    if (!open) return;
    const q = partnerQuery.trim();
    if (q.length < 1) {
      setSearchingPartners(false);
      return undefined;
    }
    let cancelled = false;
    setSearchingPartners(true);
    const timer = setTimeout(() => {
      getSessionPartners({
        gr_id: grId || undefined,
        club_id: clubId || undefined,
        q,
      })
        .then((partnerRes) => {
          if (cancelled) return;
          const extra =
            partnerRes?.data?.data?.partners || partnerRes?.data?.partners || [];
          setPartners((prev) => {
            const seen = new Set();
            const merged = [];
            extra.concat(prev).forEach((p) => {
              const id = String(p?.player_id || "");
              if (!id || seen.has(id)) return;
              seen.add(id);
              merged.push(p);
            });
            return merged;
          });
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setSearchingPartners(false);
        });
    }, 160);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, grId, clubId, partnerQuery]);

  useEffect(() => {
    if (!open) return;
    setSelectedStart((prev) => {
      if (slotFitsMemberHours(prev, duration)) return prev;
      const next = snapToNextSlot();
      return slotFitsMemberHours(next, duration) ? next : prev;
    });
  }, [duration, open]);

  useEffect(() => {
    if (!partnerMenuOpen) return undefined;
    const onPointer = (event) => {
      if (!partnerFieldRef.current?.contains(event.target)) {
        setPartnerMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [partnerMenuOpen]);

  useEffect(() => {
    setPartnerHighlight(0);
  }, [partnerQuery]);

  const canStart =
    Boolean(tableId) &&
    !submitting &&
    !loadingSlot &&
    slotFitsMemberHours(selectedStart, duration) &&
    !isSlotInPast(selectedStart);

  const handleStart = async () => {
    if (!canStart) return;
    if (liveNow) requestLiveFullscreen();
    setSubmitting(true);
    setError("");
    try {
      const localTsId = `web-${Date.now()}`;
      const res = await startPlayerSession({
        local_ts_id: localTsId,
        table_id: tableId,
        ts_duration: duration,
        partner_player_id: partnerId || undefined,
        looking_for_partner: lookingForPartner && !partnerId,
        gr_id: grId || undefined,
        club_id: clubId || undefined,
        starts_at: selectedStart.toISOString(),
        ts_date: formatCalendarDay(selectedStart),
      });
      const data = res?.data?.data || res?.data || {};
      finishStarted(data);
    } catch (err) {
      setError(err?.message || t("playerSessionStartError"));
    } finally {
      setSubmitting(false);
    }
  };

  const finishStarted = (data, joined = false) => {
    const session = {
      ts_id: data.ts_id,
      gr_id: data.gr_id || grId,
      ts_date: data.ts_date || formatCalendarDay(selectedStart),
      ts_start_time: data.ts_start_time,
      ts_duration: data.ts_duration || duration,
      ts_session_number: data.ts_session_number,
      ts_owner_player_id: true,
      table_id: data.table?.table_id || tableId,
      starts_at: data.starts_at || selectedStart.toISOString(),
      ends_at: data.ends_at || selectedEnd.toISOString(),
      joined,
    };
    if (liveNow) {
      persistLiveTrainingSession(session, { gr_id: session.gr_id });
    }
    try {
      window.dispatchEvent(
        new CustomEvent("sessionCreated", { detail: { session } })
      );
      window.dispatchEvent(new Event("smarttt-session-changed"));
    } catch {
      /* ignore */
    }
    onStarted?.(session);
    onClose?.();
  };

  const handleJoin = async (tsId) => {
    if (!tsId || submitting || joiningTsId) return;
    if (liveNow) requestLiveFullscreen();
    setJoiningTsId(String(tsId));
    setError("");
    try {
      const res = await joinPlayerSession({ ts_id: tsId });
      const data = res?.data?.data || res?.data || {};
      finishStarted(data, true);
    } catch (err) {
      setError(err?.message || t("playerSessionJoinError"));
    } finally {
      setJoiningTsId("");
    }
  };

  const pickPartner = (p) => {
    if (!p?.player_id) return;
    setPartnerId(String(p.player_id));
    setPartnerQuery(partnerLabel(p));
    setLookingForPartner(false);
    setPartnerMenuOpen(false);
  };

  const clearPartner = () => {
    setPartnerId("");
    setPartnerQuery("");
    setPartnerMenuOpen(false);
    setPartnerHighlight(0);
  };

  const onPartnerInput = (event) => {
    const next = event.target.value;
    setPartnerQuery(next);
    setPartnerMenuOpen(true);
    if (
      partnerId &&
      foldName(next) !== foldName(partnerLabel(selectedPartner))
    ) {
      setPartnerId("");
    }
  };

  const onPartnerKeyDown = (event) => {
    if (event.key === "Escape") {
      setPartnerMenuOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setPartnerMenuOpen(true);
      setPartnerHighlight((i) =>
        matchedPartners.length ? (i + 1) % matchedPartners.length : 0
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setPartnerMenuOpen(true);
      setPartnerHighlight((i) =>
        matchedPartners.length
          ? (i - 1 + matchedPartners.length) % matchedPartners.length
          : 0
      );
      return;
    }
    if (event.key === "Enter" && partnerMenuOpen && matchedPartners[partnerHighlight]) {
      event.preventDefault();
      pickPartner(matchedPartners[partnerHighlight]);
    }
  };

  const bookedHint = useMemo(
    () => occupancy.slice(0, 8).map((row) => occupancyLabel(row, t, locale)),
    [occupancy, t, locale]
  );

  const pickSlot = (start) => {
    if (!slotFitsMemberHours(start, duration) || isSlotInPast(start)) return;
    const windowOcc = slotOccupancy({
      occupancy: weekOccupancy,
      tableCount,
      slotStart: start,
      slotEnd: slotEnd(start, duration),
    });
    if (windowOcc.status === "full") return;
    setSelectedStart(start);
  };

  const focusDay = useMemo(() => startOfDay(selectedStart), [selectedStart]);
  const mobileDaySlots = useMemo(
    () => slotStartsForDay(focusDay, duration),
    [focusDay, duration]
  );

  const pickDay = (day) => {
    const dayStart = startOfDay(day);
    const slots = slotStartsForDay(dayStart, duration);
    const sameClock = slots.find(
      (s) =>
        s.getHours() === selectedStart.getHours() &&
        s.getMinutes() === selectedStart.getMinutes()
    );
    const candidates = [sameClock, ...slots].filter(Boolean);
    for (const start of candidates) {
      if (isSlotInPast(start) || !slotFitsMemberHours(start, duration)) continue;
      const windowOcc = slotOccupancy({
        occupancy: weekOccupancy,
        tableCount,
        slotStart: start,
        slotEnd: slotEnd(start, duration),
      });
      if (windowOcc.status === "full") continue;
      setSelectedStart(start);
      return;
    }
    // Fall back to first slot of the day even if full — user can adjust.
    if (slots[0]) setSelectedStart(slots[0]);
  };

  const slotHeatFor = (start) => {
    const cellEnd = slotEnd(start, SLOT_MINUTES);
    return slotOccupancy({
      occupancy: weekOccupancy,
      tableCount,
      slotStart: start,
      slotEnd: cellEnd,
    });
  };

  const slotDisabled = (start) => {
    const windowOcc = slotOccupancy({
      occupancy: weekOccupancy,
      tableCount,
      slotStart: start,
      slotEnd: slotEnd(start, duration),
    });
    return isSlotInPast(start) || !slotFitsMemberHours(start, duration) || windowOcc.status === "full";
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      className={`pnts-root ${themeMode === "light" ? "light-mode" : "dark-mode"}`}
      PaperProps={{
        className: `pnts-dialog pnts-dialog--${variant} pnts-dialog--${themeMode}`,
        "aria-labelledby": "pnts-title",
      }}
      sx={{
        "& .MuiDialog-container": {
          "@media (max-width:720px)": {
            alignItems: "stretch",
          },
        },
      }}
    >
      <div className="pnts">
        <header className="pnts__header">
          <div className="pnts__hero" aria-hidden>
            <SportsTennisIcon />
          </div>
          <div className="pnts__header-text">
            <h2 id="pnts-title" className="pnts__title">
              {t("playerNewTrainingSession")}
            </h2>
            <p className="pnts__hint">
              {t("playerNewTrainingSessionHint")}
            </p>
          </div>
          <div
            className="pnts__theme"
            role="group"
            aria-label={t("themeMode")}
          >
            <button
              type="button"
              className={`pnts__theme-btn${themeMode === "light" ? " is-active" : ""}`}
              aria-pressed={themeMode === "light"}
              title={t("lightMode")}
              onClick={() => setThemeMode("light")}
            >
              <Sun size={16} strokeWidth={2.2} />
            </button>
            <button
              type="button"
              className={`pnts__theme-btn${themeMode === "dark" ? " is-active" : ""}`}
              aria-pressed={themeMode === "dark"}
              title={t("darkMode")}
              onClick={() => setThemeMode("dark")}
            >
              <Moon size={16} strokeWidth={2.2} />
            </button>
          </div>
          <button
            type="button"
            className="pnts__close"
            onClick={onClose}
            disabled={submitting}
            aria-label={t("cancel")}
          >
            <X size={20} strokeWidth={2.2} />
          </button>
        </header>

        <div className="pnts__body">
        <div className="pnts-week">
          <div className="pnts-week__nav">
            <button
              type="button"
              className="pnts-week__nav-btn"
              onClick={() => setWeekStart((prev) => addWeeks(prev, -1))}
              disabled={!canGoPrev}
              aria-label={t("playerSessionPrevWeek")}
            >
              <ChevronLeft size={18} />
            </button>
            <div className="pnts-week__nav-label">
              <span className="pnts-week__title">{t("playerSessionWeek")}</span>
              <strong>{formatWeekRange(weekStart, locale)}</strong>
            </div>
            <button
              type="button"
              className="pnts-week__today"
              onClick={() => {
                const next = snapToNextSlot();
                setSelectedStart(next);
                setWeekStart(startOfWeekMonday(next));
              }}
            >
              {t("playerSessionThisWeek")}
            </button>
            <button
              type="button"
              className="pnts-week__nav-btn"
              onClick={() => setWeekStart((prev) => addWeeks(prev, 1))}
              disabled={!canGoNext}
              aria-label={t("playerSessionNextWeek")}
            >
              <ChevronRight size={18} />
            </button>
          </div>
          <p className="pnts-week__hours">
            {t("playerSessionHoursHint", {
              start: `${String(MEMBER_HOURS_START).padStart(2, "0")}:00`,
              end: `${String(MEMBER_HOURS_END).padStart(2, "0")}:00`,
            })}
          </p>

          {/* Mobile: pick a day, then a time — full-width taps */}
          <div className="pnts-week__mobile">
            <div
              className="pnts-week__daystrip"
              role="tablist"
              aria-label={t("playerSessionWeek")}
            >
              {days.map((day) => {
                const isToday = toDateKey(day) === toDateKey(new Date());
                const isFocus = toDateKey(day) === toDateKey(focusDay);
                return (
                  <button
                    key={toDateKey(day)}
                    type="button"
                    role="tab"
                    aria-selected={isFocus}
                    className={`pnts-week__daychip${isFocus ? " is-selected" : ""}${
                      isToday ? " is-today" : ""
                    }`}
                    onClick={() => pickDay(day)}
                  >
                    <span className="pnts-week__dow">
                      {day.toLocaleDateString(locale, { weekday: "short" })}
                    </span>
                    <span className="pnts-week__dom">{day.getDate()}</span>
                  </button>
                );
              })}
            </div>
            <div
              className="pnts-week__timelist"
              role="listbox"
              aria-label={t("playerSessionSelectedSlot")}
            >
              {loadingWeek ? (
                <p className="pnts__status pnts-week__loading">{t("loading")}</p>
              ) : (
                mobileDaySlots.map((start) => {
                  const heat = slotHeatFor(start);
                  const disabled = slotDisabled(start);
                  const selected = start.getTime() === selectedStart.getTime();
                  const end = slotEnd(start, duration);
                  return (
                    <button
                      key={start.toISOString()}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      disabled={disabled}
                      className={`pnts-week__timebtn pnts-week__timebtn--${heat.status}${
                        selected ? " is-selected" : ""
                      }`}
                      onClick={() => pickSlot(start)}
                    >
                      <span className="pnts-week__timebtn-range">
                        {formatSlotTime(start, locale)}–{formatSlotTime(end, locale)}
                      </span>
                      <span className="pnts-week__timebtn-meta">
                        {heat.free}/{heat.total} {t("playerSessionTablesFree")}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="pnts-week__scroll pnts-week__scroll--desktop" role="grid" aria-busy={loadingWeek}>
            {loadingWeek ? (
              <p className="pnts__status pnts-week__loading">{t("loading")}</p>
            ) : null}
            <div
              className="pnts-week__grid"
              style={{
                gridTemplateRows: `36px repeat(${heatRows.length}, minmax(20px, 1fr))`,
              }}
            >
              <div className="pnts-week__corner" />
              {days.map((day) => {
                const isToday = toDateKey(day) === toDateKey(new Date());
                return (
                  <div
                    key={toDateKey(day)}
                    className={`pnts-week__day${isToday ? " is-today" : ""}`}
                  >
                    <span className="pnts-week__dow">
                      {day.toLocaleDateString(locale, { weekday: "short" })}
                    </span>
                    <span className="pnts-week__dom">{day.getDate()}</span>
                  </div>
                );
              })}
              {heatRows.map((rowStart) => {
                const timeLabel = formatSlotTime(rowStart, locale);
                return (
                  <React.Fragment key={rowStart.getHours() * 60 + rowStart.getMinutes()}>
                    <div className="pnts-week__time">{timeLabel}</div>
                    {days.map((day) => {
                      const start = new Date(
                        day.getFullYear(),
                        day.getMonth(),
                        day.getDate(),
                        rowStart.getHours(),
                        rowStart.getMinutes(),
                        0,
                        0
                      );
                      const heat = slotHeatFor(start);
                      const windowOcc = slotOccupancy({
                        occupancy: weekOccupancy,
                        tableCount,
                        slotStart: start,
                        slotEnd: slotEnd(start, duration),
                      });
                      const past = isSlotInPast(start);
                      const fits = slotFitsMemberHours(start, duration);
                      const selected =
                        start.getTime() >= selectedStart.getTime() &&
                        start.getTime() < selectedEnd.getTime() &&
                        toDateKey(start) === toDateKey(selectedStart);
                      const disabled =
                        past || !fits || windowOcc.status === "full";
                      const occupants = [];
                      const seenWho = new Set();
                      for (const row of weekOccupancy) {
                        if (!row?.starts_at) continue;
                        if (
                          !intervalsOverlap(
                            row.starts_at,
                            row.ends_at,
                            start,
                            slotEnd(start, SLOT_MINUTES)
                          )
                        ) {
                          continue;
                        }
                        const who =
                          row.kind === "rental"
                            ? t("playerSessionRental")
                            : row.looking_for_partner
                              ? row.owner_name
                                ? `${row.owner_name} · ${t("playerSessionLookingForPartner")}`
                                : t("playerSessionLookingForPartner")
                              : row.owner_name ||
                                row.group_name ||
                                (row.ts_owner_player_id
                                  ? t("playerSessionPersonal")
                                  : t("playerSessionGroup"));
                        if (who && !seenWho.has(who)) {
                          seenWho.add(who);
                          occupants.push(who);
                        }
                      }
                      const label = `${day.toLocaleDateString(locale, {
                        weekday: "long",
                        day: "numeric",
                        month: "short",
                      })} ${timeLabel}, ${heat.free}/${heat.total} ${t(
                        "playerSessionTablesFree"
                      )}${occupants.length ? ` — ${occupants.join(", ")}` : ""}`;
                      return (
                        <button
                          key={`${toDateKey(day)}-${timeLabel}`}
                          type="button"
                          className={`pnts-week__cell pnts-week__cell--${heat.status}${
                            selected ? " is-selected" : ""
                          }${disabled ? " is-disabled" : ""}`}
                          disabled={disabled}
                          aria-pressed={selected}
                          aria-label={label}
                          title={label}
                          onClick={() => pickSlot(start)}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <ul className="pnts-week__legend">
            <li>
              <span className="pnts-week__swatch pnts-week__swatch--open" />
              {t("playerSessionSlotOpen")}
            </li>
            <li>
              <span className="pnts-week__swatch pnts-week__swatch--mixed" />
              {t("playerSessionSlotMixed")}
            </li>
            <li>
              <span className="pnts-week__swatch pnts-week__swatch--full" />
              {t("playerSessionSlotFull")}
            </li>
          </ul>
        </div>

        <p className="pnts__selected">
          {t("playerSessionSelectedSlot")}:{" "}
          <strong>
            {selectedStart.toLocaleDateString(locale, {
              weekday: "short",
              day: "numeric",
              month: "short",
            })}{" "}
            {formatSlotTime(selectedStart, locale)}–{formatSlotTime(selectedEnd, locale)}
          </strong>
        </p>

        <label className="pnts__label" htmlFor="pnts-duration">
          {t("duration")}
        </label>
        <div className="pnts__chips" role="listbox" aria-label={t("duration")}>
          {DURATION_OPTIONS.map((mins) => (
            <button
              key={mins}
              type="button"
              id={mins === duration ? "pnts-duration" : undefined}
              className={`pnts__chip ${mins === duration ? "is-selected" : ""}`}
              aria-pressed={mins === duration}
              onClick={() => setDuration(mins)}
            >
              {mins} {t("min")}
            </button>
          ))}
        </div>

        <label className="pnts__label" htmlFor="pnts-table">
          {t("playerSessionPickTable")}
        </label>
        {loadingSlot ? (
          <p className="pnts__status">{t("loading")}</p>
        ) : error ? null : freeTables.length === 0 ? (
          <p className="pnts__status pnts__status--warn">
            {t("playerSessionNoFreeTables")}
          </p>
        ) : (
          <select
            id="pnts-table"
            className="pnts__select"
            value={tableId}
            onChange={(e) => setTableId(e.target.value)}
          >
            {freeTables.map((tb) => (
              <option key={tb.table_id} value={tb.table_id}>
                {t("playerSessionTable")} {tb.table_number}
                {tb.table_description ? ` — ${tb.table_description}` : ""}
              </option>
            ))}
          </select>
        )}

        {bookedHint.length > 0 ? (
          <ul className="pnts__booked">
            {bookedHint.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : null}

        <label className="pnts__label" htmlFor="pnts-partner-search">
          {t("playerSessionPartnerOptional")}
        </label>
        <p className="pnts__hint pnts__hint--inline">
          {t("playerSessionPartnerClubHint")}
        </p>
        {slotSeekers.length ? (
          <div className="pnts-seekers">
            <p className="pnts__label">{t("playerSessionSeekers")}</p>
            <ul className="pnts-seekers__list">
              {slotSeekers.map((row) => (
                <li key={row.ts_id} className="pnts-seekers__item">
                  <span className="pnts-seekers__name">
                    {row.owner_name || t("playerSessionLookingForPartner")}
                    {row.table_number != null
                      ? ` · ${t("playerSessionTable")} ${row.table_number}`
                      : ""}
                  </span>
                  <button
                    type="button"
                    className="pnts__btn pnts__btn--primary pnts-seekers__join"
                    disabled={Boolean(joiningTsId) || submitting}
                    onClick={() => handleJoin(row.ts_id)}
                  >
                    {joiningTsId === String(row.ts_id)
                      ? t("loading")
                      : t("playerSessionJoinPartner")}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <label className="pnts-partner__seek">
          <input
            type="checkbox"
            checked={lookingForPartner}
            onChange={(e) => {
              const on = e.target.checked;
              setLookingForPartner(on);
              if (on) {
                setPartnerId("");
                setPartnerQuery("");
                setPartnerMenuOpen(false);
              }
            }}
          />
          <span>
            {t("playerSessionLookingForPartner")}
            <em>{t("playerSessionLookingForPartnerHint")}</em>
          </span>
        </label>
        <div className="pnts-partner" ref={partnerFieldRef}>
          <div className={`pnts-partner__field${partnerId ? " is-selected" : ""}`}>
            <Search className="pnts-partner__icon" size={18} aria-hidden />
            <input
              id="pnts-partner-search"
              className="pnts-partner__input"
              type="text"
              role="combobox"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              placeholder={t("playerSessionPartnerSearch")}
              value={partnerQuery}
              aria-expanded={partnerMenuOpen}
              aria-controls="pnts-partner-list"
              aria-autocomplete="list"
              aria-activedescendant={
                partnerMenuOpen && matchedPartners[partnerHighlight]
                  ? `pnts-partner-opt-${matchedPartners[partnerHighlight].player_id}`
                  : undefined
              }
              onChange={onPartnerInput}
              onFocus={() => {
                if (partnerQuery.trim()) setPartnerMenuOpen(true);
              }}
              onKeyDown={onPartnerKeyDown}
            />
            {partnerQuery || partnerId ? (
              <button
                type="button"
                className="pnts-partner__clear"
                aria-label={t("playerSessionPartnerClear")}
                onClick={clearPartner}
              >
                <X size={16} />
              </button>
            ) : null}
          </div>
          {partnerMenuOpen && partnerQuery.trim() ? (
            <ul
              id="pnts-partner-list"
              className="pnts-partner__list"
              role="listbox"
            >
              {matchedPartners.length ? (
                matchedPartners.map((p, idx) => (
                  <li key={p.player_id} role="presentation">
                    <button
                      type="button"
                      id={`pnts-partner-opt-${p.player_id}`}
                      role="option"
                      className={`pnts-partner__option${
                        idx === partnerHighlight ? " is-active" : ""
                      }${String(p.player_id) === String(partnerId) ? " is-picked" : ""}`}
                      aria-selected={String(p.player_id) === String(partnerId)}
                      onMouseEnter={() => setPartnerHighlight(idx)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => pickPartner(p)}
                    >
                      <span className="pnts-partner__name">{partnerLabel(p)}</span>
                    </button>
                  </li>
                ))
              ) : (
                <li className="pnts-partner__empty">
                  {searchingPartners ? t("loading") : t("playerSessionPartnerNoMatch")}
                </li>
              )}
            </ul>
          ) : null}
        </div>
        <p className="pnts__hint pnts__hint--inline">
          {partnerId
            ? partnerLabel(selectedPartner || { player_id: partnerId })
            : t("playerSessionNoPartner")}
        </p>

        {error ? (
          <p className="pnts__error" role="alert">
            {error}
          </p>
        ) : null}
        </div>

        <div className="pnts__actions">
          <button
            type="button"
            className="pnts__btn pnts__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            className="pnts__btn pnts__btn--primary"
            onClick={handleStart}
            disabled={!canStart}
          >
            {submitting
              ? liveNow
                ? t("starting")
                : t("playerSessionReserving")
              : liveNow
                ? t("playerSessionStart")
                : t("playerSessionReserve")}
          </button>
        </div>
        {!isMobileDevice() ? (
          <p className="pnts__footnote">{t("playerSessionOpenOnPhone")}</p>
        ) : null}
      </div>
    </Dialog>
  );
}

export default PlayerNewTrainingSessionDialog;
